/* Adaptador CLIENTE de worldathletics.org (World Athletics).
 *
 * Los resultados de un campeonato WA salen del GraphQL público de su web
 * (AppSync; endpoint y api-key van embebidos en los bundles JS públicos de
 * worldathletics.org, verificado 2026-07-29 con el Mundial sub-20 Lima 2024).
 * La query `getCalendarCompetitionResults(competitionId, day)` devuelve, por
 * día, cada prueba con sus series/grupos ("races") y los resultados de cada
 * una (marca, viento, país, récords) — es el ARCHIVO del calendario, que
 * durante un campeonato en curso se va rellenando según acaban las pruebas.
 * (El sistema "live" de WA —getLiveResultsByUnitIds, con intentos y Q/q en
 * vivo— solo está poblado durante la competición; se integrará cuando el CIS
 * de Oregon26 cargue. Los `qualified` del archivo vienen incompletos, así
 * que las tarjetas WA van sin chips Q/q.)
 *
 * CORS abierto (`access-control-allow-origin: *`) → se llama desde el
 * navegador del admin, como la fuente EA. Keys propias:
 * "WA:<competitionId>:<day>:<eventId>:<raceId>".
 */

import type { EventsResponse, EventDetail, PodioRunner, Evento } from '@/lib/api';

const EP = 'https://graphql-prod-4877.edge.aws.worldathletics.org/graphql';
// Api-key PÚBLICA (va en el JS de worldathletics.org para todo visitante).
const KEY = 'da2-tzmostylynabpfkrgbmmml4toq';

// ── CIS LIVE (2026-08-05): el feed del calendario NUNCA cargó para Oregon26,
// así que la capa de datos se cambia a la API del directo (la de la página
// /live de worldathletics.org, endpoint+key públicos de sus bundles). Se
// construye aquí el MISMO modelo WADia que devolvía el calendario y todo lo
// de abajo (tarjetas, filtro ESP, resumen) queda igual. En concursos EN CURSO
// solo se incluyen los atletas con marca → el botón manual publica el corte
// provisional (como rfealive en el Absoluto).
const EP_CIS = 'https://fam4j72f5bgwlndqx2hgrkfqei.appsync-api.us-east-1.amazonaws.com/graphql';
const KEY_CIS = 'da2-cbvvbexi4zggvbehcykrs3bvba';

const Q_PHASES = `query GetPhases($eventId: Int!, $isLiveSession: Boolean!) {
  getPhases(eventId: $eventId, isLiveSession: $isLiveSession) {
    id dayNum phaseName status sexCode disciplineName phaseDateAndTime
    isStartlistPublished isResultPublished isLiveLeaking isCombined isRelay
  }
}`;

const Q_INT = `query GetLiveIntermediate($phaseId: Int) {
  getLiveIntermediate(phaseId: $phaseId) {
    unitId intermediateOrder mark splitName
    competitorForename competitorSurname competitorTeamName
  }
}`;

const Q_LIVE = `query GetLiveResult($phaseId: Int, $competitionId: Int) {
  getLiveResult(phaseId: $phaseId, competitionId: $competitionId) {
    type unitId unitName bib mark rank wind record qualified resultStatus
    noStart lane competitorForename competitorSurname competitorTeamName
    competitorCountryCode competitorType
    teamMembers { competitorForename competitorSurname }
  }
}`;

const QUERY_SLUG = `query getWawEvent($nameUrlSlug: String) {
  getWawEvent(nameUrlSlug: $nameUrlSlug) { id eventId_WA name venue startDate }
}`;

async function gql<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const r = await fetch(EP, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': KEY },
    body: JSON.stringify({ query, variables }),
  });
  if (!r.ok) throw new Error(`WA GraphQL ${r.status}`);
  const j = await r.json();
  if (j.errors?.length) throw new Error(`WA GraphQL: ${j.errors[0].message}`);
  return j.data as T;
}

export function esUrlWA(url: string): boolean {
  try {
    return new URL(url).hostname.endsWith('worldathletics.org');
  } catch {
    return false;
  }
}

/** ".../competitions/<serie>/<evento-o-slug>/..." → competitionId numérico.
 *  El slug puede llevar el id al final ("...-7180313") o ser un alias
 *  ("oregon26") que se resuelve con getWawEvent. */
// Alias conocidos sin id en el slug (getWawEvent no siempre los resuelve;
// el id sale del __NEXT_DATA__ de la página del campeonato).
const slugId: Record<string, number> = { oregon26: 7204186 };

async function competitionId(url: string): Promise<number> {
  const u = new URL(url);
  const forzado = u.searchParams.get('id');
  if (forzado) return parseInt(forzado, 10);
  const partes = u.pathname.split('/').filter(Boolean);
  const slug = partes[2] ?? partes[partes.length - 1] ?? '';
  const m = /-(\d{5,})$/.exec(slug);
  if (m) return parseInt(m[1], 10);
  if (slugId[slug]) return slugId[slug];
  const d = await gql<{ getWawEvent?: { eventId_WA?: number } }>(QUERY_SLUG, { nameUrlSlug: slug });
  const id = d.getWawEvent?.eventId_WA;
  if (!id) throw new Error(`No se pudo resolver el campeonato WA '${slug}'`);
  slugId[slug] = id;
  return id;
}

interface WAResult {
  competitor: { name?: string; birthDate?: string; teamMembers?: { name?: string }[] | null };
  mark?: string; nationality?: string; place?: string; qualified?: string | boolean | null;
  records?: string; wind?: string | null; remark?: string | null; points?: number | null;
}
interface WARace {
  race: string; raceId?: number | null; raceNumber?: number | null; date?: string; day?: number;
  results?: WAResult[] | null; startList?: { competitor?: { name?: string; country?: string } }[] | null;
  /** false = concurso/serie EN CURSO (corte provisional con los que ya tienen marca). */
  _oficial?: boolean;
  /** hora local de la fase (ordenar el listado: lo más reciente primero). */
  _ts?: string;
}
interface WAEvent { event: string; eventId: number; gender: string; isRelay?: boolean; races?: WARace[] | null }
interface WADia {
  competition?: { name?: string; venue?: string; startDate?: string };
  options?: { days?: { date?: string; day: number }[] };
  eventTitles?: { eventTitle?: string; events?: WAEvent[] | null }[];
}

// ── Constructor del modelo WADia desde el CIS live ───────────────────────────

interface CISPhase {
  id: number; dayNum: number; phaseName: string; status: number; sexCode: string;
  disciplineName: string; phaseDateAndTime?: string; isStartlistPublished?: boolean;
  isResultPublished?: boolean; isLiveLeaking?: boolean; isCombined?: boolean; isRelay?: boolean;
}
interface CISRow {
  type?: string; unitId?: number | null; unitName?: string | null; bib?: string | null;
  mark?: string | null; rank?: string | null; wind?: string | null; record?: string | null;
  qualified?: string | null; resultStatus?: string | null; noStart?: boolean | null;
  lane?: number | null; competitorForename?: string | null; competitorSurname?: string | null;
  competitorTeamName?: string | null; competitorCountryCode?: string | null; competitorType?: string | null;
  teamMembers?: { competitorForename?: string | null; competitorSurname?: string | null }[] | null;
}

const COMP_META: Record<number, { name: string; venue: string; startDate: string }> = {
  7204186: { name: 'World Athletics U20 Championships', venue: 'Oregon', startDate: '2026-08-05' },
};

async function gqlCIS<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const r = await fetch(EP_CIS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': KEY_CIS },
    body: JSON.stringify({ query, variables }),
  });
  if (!r.ok) throw new Error(`WA live ${r.status}`);
  const j = await r.json();
  if (j.errors?.length) throw new Error(`WA live: ${j.errors[0].message}`);
  return j.data as T;
}

let phasesCache: Promise<CISPhase[]> | null = null;
function fasesCIS(compId: number): Promise<CISPhase[]> {
  if (!phasesCache) {
    phasesCache = gqlCIS<{ getPhases: CISPhase[] }>(Q_PHASES, { eventId: compId, isLiveSession: false })
      .then((d) => d.getPhases ?? [])
      .catch((e) => { phasesCache = null; throw e; });
  }
  return phasesCache;
}

function nombreCIS(r: CISRow): string {
  if (r.competitorType === 'T' || (r.competitorTeamName && !r.competitorSurname)) {
    return (r.competitorTeamName ?? '').trim();
  }
  return `${r.competitorForename ?? ''} ${r.competitorSurname ?? ''}`.trim();
}

/** id numérico estable por (disciplina, sexo) para agrupar rondas en eventos. */
function eventIdDe(disciplina: string, sexo: string): number {
  let h = 0;
  for (const c of `${disciplina}|${sexo}`) h = (h * 31 + c.charCodeAt(0)) % 1_000_000_007;
  return h;
}

function fechaCIS(iso?: string): string {
  // "2026-08-05T10:22:00.000" → "5 AUG 2026" (el formato que espera fechaES)
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return '';
  const mes = Object.keys(MESES).find((k) => MESES[k] === m[2]) ?? '';
  return `${parseInt(m[3], 10)} ${mes} ${m[1]}`;
}

// TTL corto: en directo el usuario abre cortes repetidamente y los datos
// cambian cada minuto — sin TTL, la pestaña se quedaba con la primera foto.
const diaCache: Record<string, { ts: number; p: Promise<WADia> }> = {};
const DIA_TTL_MS = 60_000;

function dia(compId: number, day: number): Promise<WADia> {
  const k = `${compId}:${day}`;
  const c = diaCache[k];
  if (!c || Date.now() - c.ts > DIA_TTL_MS) {
    phasesCache = null; // las fases también cambian (status/isResultPublished)
    diaCache[k] = {
      ts: Date.now(),
      p: construirDia(compId, day).catch((e) => { delete diaCache[k]; throw e; }),
    };
  }
  return diaCache[k].p;
}

async function construirDia(compId: number, day: number): Promise<WADia> {
  const fases = await fasesCIS(compId);
  const delDia = fases.filter((f) => f.dayNum === day
    && (f.isStartlistPublished || f.isResultPublished || f.isLiveLeaking || f.status !== 2));
  // eventos agrupados por disciplina+sexo (las rondas de un evento van juntas)
  const eventos = new Map<number, WAEvent>();
  await Promise.all(delDia.map(async (f) => {
    let rows: CISRow[] = [];
    try {
      const d = await gqlCIS<{ getLiveResult: CISRow[] }>(Q_LIVE, { phaseId: f.id, competitionId: compId });
      rows = d.getLiveResult ?? [];
    } catch { return; }
    const porUnidad = new Map<number, { res: CISRow[]; sl: CISRow[] }>();
    for (const r of rows) {
      // Solo resultados y startlists: el CIS también manda filas de PARCIALES
      // (pasos por 400/800/1200 en relevos, splits en fondo) con otros `type`
      // que duplicarían a cada equipo/atleta en la tarjeta.
      if (r.type !== 'result' && r.type !== 'startlist') continue;
      const uid = r.unitId ?? 0;
      if (!porUnidad.has(uid)) porUnidad.set(uid, { res: [], sl: [] });
      porUnidad.get(uid)![r.type === 'startlist' ? 'sl' : 'res'].push(r);
    }
    const raceName = (f.phaseName || 'Final').replace('Semi-Final', 'Semifinal');
    const evId = eventIdDe(f.disciplineName, f.sexCode);
    if (!eventos.has(evId)) {
      eventos.set(evId, { event: f.disciplineName, eventId: evId, gender: f.sexCode, races: [] });
    }
    const unidades = [...porUnidad.entries()].sort((a, b) => a[0] - b[0]);
    unidades.forEach(([uid, u], idx) => {
      const conMarca = u.res.filter((r) => (r.mark ?? '').trim() || r.noStart);
      if (!conMarca.length && !u.sl.length) return;
      const completa = conMarca.length > 0 && conMarca.length === u.res.length;
      // serie/grupo EN CURSO: corte provisional solo con los que tienen marca
      const rango = (r: CISRow) => {
        const n = parseInt(String(r.rank ?? ''), 10);
        return Number.isFinite(n) && n > 0 ? n : 999; // DQ/DNF/DNS al final
      };
      const filas = (completa ? u.res : u.res.filter((r) => (r.mark ?? '').trim()))
        .slice()
        .sort((a, b) => rango(a) - rango(b))
        .map((r): WAResult => ({
          competitor: {
            name: nombreCIS(r),
            teamMembers: (r.teamMembers ?? []).map((m) => ({
              name: `${m.competitorForename ?? ''} ${m.competitorSurname ?? ''}`.trim(),
            })),
          },
          mark: (r.mark ?? '').trim(),
          nationality: r.competitorCountryCode ?? '',
          place: String(r.rank ?? ''),
          // el CIS SÍ trae los Q/q fiables (el archivo del calendario no)
          qualified: (r.qualified ?? '').trim() || null,
          records: r.record ?? '',
          wind: r.wind ?? '',
          remark: r.noStart && !(r.mark ?? '').trim() ? 'DNS' : '',
          points: null,
        }));
      eventos.get(evId)!.races!.push({
        race: raceName,
        raceId: f.id * 1000 + uid,
        raceNumber: idx + 1,
        date: fechaCIS(f.phaseDateAndTime),
        day,
        results: filas,
        startList: u.sl.map((r) => ({ competitor: { name: nombreCIS(r), country: r.competitorCountryCode ?? '' } })),
        _oficial: completa,
        _ts: f.phaseDateAndTime ?? '',
      });
    });
  }));
  const meta = COMP_META[compId] ?? { name: 'World Athletics', venue: '', startDate: '' };
  const dias = [...new Set(fases.map((f) => f.dayNum))].sort((a, b) => a - b);
  return {
    competition: meta,
    options: {
      days: dias.map((n) => ({
        day: n,
        date: fechaCIS(fases.find((f) => f.dayNum === n)?.phaseDateAndTime),
      })),
    },
    eventTitles: [{ eventTitle: meta.name, events: [...eventos.values()] }],
  };
}

const MESES: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

function fechaES(s: string): string {
  const m = /(\d{1,2})\s+([A-Z]{3})\s+(\d{4})/.exec((s || '').toUpperCase());
  return m ? `${m[1].padStart(2, '0')}/${MESES[m[2]] ?? '01'}/${m[3]}` : '';
}

function nombreES(evento: string, gender: string): string {
  const base = evento.replace(/^(Men's|Women's|Mixed)\s+/i, '');
  const gen = gender === 'M' ? 'Hombres' : gender === 'W' ? 'Mujeres' : 'Mixto';
  return `${base} ${gen}`;
}

const FASES_WA: Record<string, string> = {
  Final: 'Final', Semifinal: 'Semifinal', 'Round 1': 'Eliminatoria', 'Round 2': '2ª Ronda',
  Heats: 'Eliminatoria', 'Preliminary Round': 'Ronda previa',
  Qualification: 'Clasificación', Combined: 'Combinada', Heptathlon: 'Heptatlón', Decathlon: 'Decatlón',
};

function rondaES(race: string, n: number | null, total: number): string {
  const base = FASES_WA[(race || '').split(' - ')[0]] ?? (race || 'Final').split(' - ')[0];
  return n && total > 1 ? `${base} ${n}/${total}` : base;
}

/** EventsResponse compatible con /api/events, construido en el navegador. */
export async function fetchEventsWA(url: string): Promise<EventsResponse> {
  const compId = await competitionId(url);
  const d1 = await dia(compId, 1);
  const diasInfo = d1.options?.days ?? [{ day: 1 }];
  const dias = diasInfo.map((x) => x.day);
  const datos = await Promise.all(dias.map((n) => (n === 1 ? Promise.resolve(d1) : dia(compId, n))));
  const eventos: Evento[] = [];
  const tsDe = new Map<string, string>(); // pruebaId → hora de la fase (orden)
  for (let i = 0; i < datos.length; i++) {
    const dd = datos[i];
    for (const et of dd.eventTitles ?? []) {
      for (const e of et.events ?? []) {
        const races = e.races ?? [];
        const totalPor: Record<string, number> = {};
        for (const r of races) totalPor[r.race] = (totalPor[r.race] ?? 0) + 1;
        for (const r of races) {
          const key = `WA:${compId}:${dias[i]}:${e.eventId}:${r.raceId ?? `${r.raceNumber ?? 0}`}`;
          tsDe.set(key, r._ts ?? '');
          eventos.push({
            pruebaId: key, detalleKey: key,
            nombre: nombreES(e.event, e.gender),
            ronda: rondaES(r.race, r.raceNumber ?? null, totalPor[r.race] ?? 1),
            // La fecha por carrera viene nula en el feed por día: se usa la
            // del propio día consultado (options.days).
            fecha: fechaES(r.date ?? diasInfo[i]?.date ?? ''),
            // hora local de la fase: la vista ordena por fecha+hora (tsClave)
            hora: /T(\d{2}:\d{2})/.exec(r._ts ?? '')?.[1] ?? '',
            estado: (r.results?.length ?? 0) > 0
              ? (r._oficial === false ? 'En curso' : 'Oficial')
              : 'Lista de Salida',
            viento: '', podio: [], resultados: [],
          } as Evento);
        }
      }
    }
  }
  // Entrada sintética por jornada con resultados: "Resumen Jornada N" de los
  // españoles, para publicar en Instagram (2026-07-29; modal propio).
  for (let i = 0; i < datos.length; i++) {
    const hay = (datos[i].eventTitles ?? []).some((et) =>
      (et.events ?? []).some((e) => (e.races ?? []).some((r) => (r.results?.length ?? 0) > 0)));
    if (!hay) continue;
    eventos.push({
      pruebaId: `WARES:${compId}:${dias[i]}`, detalleKey: `WARES:${compId}:${dias[i]}`,
      nombre: `Resumen Jornada ${dias[i]}`, ronda: 'España', fecha: fechaES(diasInfo[i]?.date ?? ''),
      hora: '', estado: 'Oficial', viento: '', podio: [], resultados: [],
    } as Evento);
  }
  const comp = d1.competition;
  const anio = /\d{4}/.exec(comp?.startDate ?? '')?.[0] ?? '';
  const nombre = /U20/i.test(comp?.name ?? '') ? 'Mundial Sub-20' : (comp?.name ?? 'World Athletics');
  // Lo más RECIENTE primero (2026-08-05): en directo, la prueba que acaba de
  // aparecer va arriba. Los "Resumen Jornada N" flotan a la cabecera.
  eventos.sort((a, b) => {
    const ta = a.pruebaId?.startsWith('WARES:') ? '~' : (tsDe.get(a.pruebaId ?? '') ?? '');
    const tb = b.pruebaId?.startsWith('WARES:') ? '~' : (tsDe.get(b.pruebaId ?? '') ?? '');
    return tb.localeCompare(ta);
  });
  return {
    competicion: [nombre, comp?.venue, anio].filter(Boolean).join(' '),
    fecha: '', sede: comp?.venue ?? null, fuente: 'wa', eventos,
  } as EventsResponse;
}

const IRM_WA = new Set(['DNS', 'DNF', 'DQ', 'DSQ', 'NM', 'NH', 'DQF']);

/** EventDetail compatible con /api/event-detail para keys "WA:...". */
export async function fetchEventDetailWA(key: string): Promise<EventDetail> {
  const vacio = {
    intentos: {}, combinada: {}, clasificacion: [], salida: [], alineacion: [],
    noClasificados: [], qualiByRank: null, qualiByTime: null, avisos: {}, avisosDorsal: {}, records: {},
  } as unknown as EventDetail;
  const m = /^WA:(\d+):(\d+):(\d+):(.+)$/.exec(key);
  if (!m) return vacio;
  const [, compId, day, eventId, raceRef] = m;
  const dd = await dia(parseInt(compId, 10), parseInt(day, 10));
  let race: WARace | undefined;
  let esFinal = false;
  for (const et of dd.eventTitles ?? []) {
    for (const e of et.events ?? []) {
      if (String(e.eventId) !== eventId) continue;
      race = (e.races ?? []).find((r) => String(r.raceId ?? r.raceNumber ?? 0) === raceRef);
      esFinal = (race?.race ?? '') === 'Final';
    }
  }
  if (!race) return vacio;
  const clasificacion: PodioRunner[] = [];
  const noClasificados: EventDetail['noClasificados'] = [];
  const records: Record<string, string[]> = {};
  (race.results ?? []).forEach((res, i) => {
    const aid = `wa-${eventId}-${raceRef}-${i}`;
    const nombre = (res.competitor?.name ?? '').trim();
    const marca = (res.mark ?? '').trim();
    const irm = IRM_WA.has(marca.toUpperCase()) ? marca.toUpperCase()
      : IRM_WA.has((res.remark ?? '').toUpperCase()) ? (res.remark ?? '').toUpperCase() : null;
    if (!marca || irm) {
      noClasificados.push({
        nombre, club: res.nationality ?? '', estado: irm ?? 'DNS',
        dorsal: null, categoria: null,
      } as EventDetail['noClasificados'][number]);
      return;
    }
    const puesto = (res.place ?? '').replace(/\.$/, '').trim();
    clasificacion.push({
      puesto, nombre, atleta: nombre, club: res.nationality ?? '', pais: res.nationality ?? null,
      marca, viento: (res.wind ?? '').trim(), athId: aid, dorsal: null, categoria: null,
      quali: typeof res.qualified === 'string' && ['Q', 'q'].includes(res.qualified.trim())
        ? res.qualified.trim() : null,
      esGuest: false,
      medalla: esFinal && ['1', '2', '3'].includes(puesto) ? puesto : null,
      etiquetas: [],
    } as PodioRunner);
    const recs = (res.records ?? '').split(/[,;]+/).map((x) => x.trim()).filter(Boolean);
    if (recs.length) records[aid] = recs;
  });
  // INTENTOS de concursos desde el CIS (X = nulo, '-' = pasa, '' = pendiente):
  // el modal ya pinta los chips desde det.intentos (paridad con Málaga).
  const intentos: Record<string, { mark: string }[]> = {};
  const raceIdNum = parseInt(raceRef, 10);
  if (Number.isFinite(raceIdNum) && raceIdNum >= 1000) {
    try {
      const d = await gqlCIS<{ getLiveIntermediate: {
        unitId?: number | null; intermediateOrder?: number | null; mark?: string | null;
        splitName?: string | null;
        competitorForename?: string | null; competitorSurname?: string | null;
        competitorTeamName?: string | null;
      }[] | null }>(Q_INT, { phaseId: Math.floor(raceIdNum / 1000) });
      const unitIdNum = raceIdNum % 1000;
      const porNombre: Record<string, { o: number; m: string; h: string }[]> = {};
      for (const r of d.getLiveIntermediate ?? []) {
        if ((r.unitId ?? 0) !== unitIdNum) continue;
        const nom = `${r.competitorForename ?? ''} ${r.competitorSurname ?? ''}`.trim()
          || (r.competitorTeamName ?? '').trim();
        if (!nom) continue;
        // Chips SOLO de CONCURSO: verticales (splitName es la ALTURA,
        // "2.05") y horizontales ("Attempt N", sin altura). Todo lo demás
        // son PARCIALES de carrera ("100 m", "1000 m"… — la marcha del
        // 5000 salía con los 50 pasos como chips, 2026-08-08) y se
        // descarta: en pista solo interesa el tiempo final.
        const sp = (r.splitName ?? '').trim();
        const esAltura = /^\d+([.,]\d+)?$/.test(sp);
        const esIntento = /^attempt/i.test(sp);
        if (!esAltura && !esIntento) continue;
        const h = esAltura ? sp : '';
        (porNombre[nom] ??= []).push({ o: r.intermediateOrder ?? 0, m: (r.mark ?? '').trim(), h });
      }
      const nombreAid: Record<string, string> = {};
      for (const p of clasificacion) { if (p.athId) nombreAid[p.nombre] = p.athId; }
      for (const [nom, serie] of Object.entries(porNombre)) {
        const aid = nombreAid[nom];
        if (!aid) continue;
        serie.sort((a, b) => a.o - b.o);
        while (serie.length && serie[serie.length - 1].m === '') serie.pop();
        if (serie.length) {
          intentos[aid] = serie.map((x) => (x.h
            ? { mark: x.m || '–', height: x.h }
            : { mark: x.m || '–' }));
        }
      }
    } catch { /* sin intentos: la tarjeta sale sin chips */ }
  }
  return { ...vacio, clasificacion, noClasificados, records, intentos } as EventDetail;
}

/** Filtro de ESPAÑOLES (2026-07-29): mapa key→habilitada. En WA cada serie
 *  trae la nacionalidad en resultados y lista de salida, así que la regla
 *  ("prueba con ESP: se publica la RONDA entera mientras haya ESP en ella")
 *  se calcula directa de la caché de días, sin peticiones extra. */
export async function espPorUnidadWA(
  eventos: { detalleKey?: string | null }[],
): Promise<Record<string, boolean>> {
  const out: Record<string, boolean> = {};
  const m = /^WA:(\d+):/.exec(eventos.find((e) => e.detalleKey)?.detalleKey ?? '');
  if (!m) return out;
  const compId = parseInt(m[1], 10);
  const d1 = await dia(compId, 1);
  const dias = d1.options?.days?.map((x) => x.day) ?? [1];
  const datos = await Promise.all(dias.map((n) => (n === 1 ? Promise.resolve(d1) : dia(compId, n))));
  for (let i = 0; i < datos.length; i++) {
    for (const et of datos[i].eventTitles ?? []) {
      for (const e of et.events ?? []) {
        const races = e.races ?? [];
        const rondaEsp: Record<string, boolean> = {};
        for (const r of races) {
          const tiene = (r.results ?? []).some((x) => (x.nationality ?? '') === 'ESP')
            || (r.startList ?? []).some((x) => (x.competitor?.country ?? '') === 'ESP');
          rondaEsp[r.race] = (rondaEsp[r.race] ?? false) || tiene;
        }
        for (const r of races) {
          const key = `WA:${compId}:${dias[i]}:${e.eventId}:${r.raceId ?? `${r.raceNumber ?? 0}`}`;
          out[key] = !!rondaEsp[r.race];
        }
      }
    }
  }
  return out;
}

/** Atletas ESP de TODA la competición (startlists + resultados de todas las
 *  jornadas), para la vista /fotos-finales (tarjeta de final con foto).
 *  Individuales dedup por nombre; relevos: una entrada 'Spain' por prueba
 *  (equipos distintos → fotos distintas). En ORDEN DE PARTICIPACIÓN (hora de
 *  la primera fase en que aparece cada uno, `_ts` de la carrera), no
 *  alfabético: así se ve qué foto urge subir antes (Santi 2026-08-07). */
export async function espAtletasWA(url: string): Promise<{ nombre: string; evento: string; esRelevo: boolean }[]> {
  const compId = await competitionId(url);
  const d1 = await dia(compId, 1);
  const dias = d1.options?.days?.map((x) => x.day) ?? [1];
  const datos = await Promise.all(dias.map((n) => (n === 1 ? Promise.resolve(d1) : dia(compId, n))));
  const individuales = new Map<string, { nombre: string; evento: string; esRelevo: boolean; ts: string }>();
  const relevos = new Map<string, { nombre: string; evento: string; esRelevo: boolean; ts: string }>();
  for (const [i, dd] of datos.entries()) {
    for (const et of dd.eventTitles ?? []) {
      for (const e of et.events ?? []) {
        const evento = nombreES(e.event, e.gender);
        const esRelevo = /relay/i.test(e.event);
        for (const r of e.races ?? []) {
          // Momento de la fase, ordenable: ISO de `_ts` (trae la fecha) con
          // el nº de jornada como respaldo si viniera vacío.
          const ts = r._ts || `dia-${String(dias[i] ?? 9).padStart(2, '0')}`;
          const nombres: string[] = [];
          for (const x of r.results ?? []) {
            if ((x.nationality ?? '') === 'ESP' && x.competitor?.name) nombres.push(x.competitor.name);
          }
          for (const x of r.startList ?? []) {
            if ((x.competitor?.country ?? '') === 'ESP' && x.competitor?.name) nombres.push(x.competitor.name);
          }
          for (const n of nombres) {
            if (esRelevo) {
              const prev = relevos.get(evento);
              if (!prev || ts < prev.ts) relevos.set(evento, { nombre: 'Spain', evento, esRelevo: true, ts });
            } else {
              const prev = individuales.get(n);
              if (!prev || ts < prev.ts) individuales.set(n, { nombre: n, evento, esRelevo: false, ts });
            }
          }
        }
      }
    }
  }
  return [...individuales.values(), ...relevos.values()]
    .sort((a, b) => a.ts.localeCompare(b.ts) || a.nombre.localeCompare(b.nombre, 'es'))
    .map(({ ts: _ts, ...resto }) => resto);
}

// ─── Resumen de jornada de los ESPAÑOLES (para Instagram) ───────────────────

/** Añade a fetchEventsWA una entrada sintética por jornada con resultados.
 *  Key: "WARES:<compId>:<day>" (no colisiona con el regex de detalle WA). */
export function esKeyResumenWA(key: string | null | undefined): boolean {
  return (key ?? '').startsWith('WARES:');
}

export interface FilaResumen {
  puesto: string; nombre: string; club: string; marca: string; pais: string;
  estado: string; clasificado: boolean; eliminado: boolean;
  /** "1"/"2"/"3" en finales: la tarjeta pinta la medalla en el puesto. */
  medalla: string | null;
}

/** Actuaciones de los ESP en la jornada, con su "qué pasó": medalla/puesto en
 *  finales, "Clasificado/a a <ronda>" si aparece en una ronda posterior del
 *  mismo evento (resultados o lista de salida), "Eliminado/a" si no. */
export async function resumenJornadaWA(key: string, parte?: 'manana' | 'tarde' | ''): Promise<{
  titulo: string; subtitulo: string; competicion: string; filas: FilaResumen[];
}> {
  const m = /^WARES:(\d+):(\d+)$/.exec(key);
  if (!m) return { titulo: '', subtitulo: '', competicion: '', filas: [] };
  const compId = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const d1 = await dia(compId, 1);
  const diasInfo = d1.options?.days ?? [{ day: 1 }];
  const datos = await Promise.all(diasInfo.map((x) => (x.day === 1 ? Promise.resolve(d1) : dia(compId, x.day))));
  // Rondas posteriores de cada evento (día > o mismo día tras la ronda):
  // aparición del ATLETA (por nombre) en resultados o lista de salida.
  const aparicionesPorEvento = new Map<number, { dia: number; ronda: string; nombres: Set<string> }[]>();
  for (let i = 0; i < datos.length; i++) {
    for (const et of datos[i].eventTitles ?? []) {
      for (const e of et.events ?? []) {
        for (const r of e.races ?? []) {
          const nombres = new Set<string>([
            ...(r.results ?? []).map((x) => (x.competitor?.name ?? '').trim()),
            ...(r.startList ?? []).map((x) => (x.competitor?.name ?? '').trim()),
          ].filter(Boolean));
          if (!aparicionesPorEvento.has(e.eventId)) aparicionesPorEvento.set(e.eventId, []);
          aparicionesPorEvento.get(e.eventId)!.push({ dia: diasInfo[i].day, ronda: r.race, nombres });
        }
      }
    }
  }
  const ordenFase = (race: string) => (/Final$/.test(race) ? 9 : /Semifinal/.test(race) ? 5 : 1);
  const filasTs: { fila: FilaResumen; ts: string }[] = [];
  const dd = datos[diasInfo.findIndex((x) => x.day === day)];
  for (const et of dd?.eventTitles ?? []) {
    for (const e of et.events ?? []) {
      const races = e.races ?? [];
      const totalPor: Record<string, number> = {};
      for (const r of races) totalPor[r.race] = (totalPor[r.race] ?? 0) + 1;
      for (const r of races) {
        // parte: sesión de Eugene (mañana <16h local / tarde) para publicar
        // el resumen de UNA sesión (los parciales de WhatsApp).
        if (parte) {
          const hora = parseInt(/T(\d{2})/.exec(r._ts ?? '')?.[1] ?? '-1', 10);
          if (parte === 'manana' && !(hora >= 0 && hora < 16)) continue;
          if (parte === 'tarde' && hora < 16) continue;
        }
        for (const res of r.results ?? []) {
          if ((res.nationality ?? '') !== 'ESP') continue;
          const nombre = (res.competitor?.name ?? '').trim();
          const fem = e.gender === 'W';
          const puesto = (res.place ?? '').replace(/\.$/, '').trim();
          const esFinal = r.race === 'Final';
          let estado = '';
          let clasificado = false;
          let eliminado = false;
          let medalla: string | null = null;
          if (esFinal) {
            // Medalla en el PUESTO (como las tarjetas de finales); sin chip.
            medalla = ['1', '2', '3'].includes(puesto) ? puesto : null;
            estado = medalla ? '' : puesto ? `${puesto}º en la Final` : 'Final';
            clasificado = !!medalla;
          } else {
            // 1º los Q/q OFICIALES de la ronda: la siguiente puede no tener
            // startlist aún y el cruce de apariciones daría 'Eliminado' en
            // falso (pasó con el 800 la noche del día 1).
            const qFlag = typeof res.qualified === 'string'
              && ['Q', 'q'].includes(res.qualified.trim());
            const posteriores = (aparicionesPorEvento.get(e.eventId) ?? []).filter(
              (a) => ordenFase(a.ronda) > ordenFase(r.race) && a.nombres.has(nombre));
            if (qFlag || posteriores.length) {
              const sig = posteriores.sort((a, b) => ordenFase(a.ronda) - ordenFase(b.ronda))[0];
              const sigNombre = sig ? (FASES_WA[(sig.ronda || '').split(' - ')[0]] ?? sig.ronda) : '';
              estado = `${fem ? 'Clasificada' : 'Clasificado'}${sigNombre ? ` a ${sigNombre}` : ''}`;
              clasificado = true;
            } else {
              estado = fem ? 'Eliminada' : 'Eliminado';
              eliminado = true;
            }
          }
          filasTs.push({
            ts: r._ts ?? '',
            fila: {
              puesto, nombre,
              club: `${nombreES(e.event, e.gender)} · ${rondaES(r.race, r.raceNumber ?? null, totalPor[r.race] ?? 1)}`,
              marca: (res.mark ?? '').trim(), pais: 'ESP', estado, clasificado, eliminado, medalla,
            },
          });
        }
      }
    }
  }
  // Orden CRONOLOGICO (2026-08-06: el resumen manual mezclaba manana y
  // tarde): por hora local de la fase.
  filasTs.sort((a, b) => a.ts.localeCompare(b.ts));
  const filas = filasTs.map((x) => x.fila);
  const fecha = fechaES(diasInfo.find((x) => x.day === day)?.date ?? '');
  const comp = d1.competition;
  const anio = /\d{4}/.exec(comp?.startDate ?? '')?.[0] ?? '';
  return {
    titulo: 'Resumen de los españoles',
    subtitulo: `Jornada ${day} · ${fecha}`,
    competicion: [/U20/i.test(comp?.name ?? '') ? 'Mundial Sub-20' : comp?.name ?? '', comp?.venue, anio].filter(Boolean).join(' '),
    filas,
  };
}
